import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  HTMLAttributes,
  useState,
  FocusEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Icon, IconType } from '../Icon';
import { SelectInputitem, InputStateTypes } from '@/interface';
import { Text } from '../Text';
import { cn } from '@/utils/common_functions';
import { useClickOutside } from '@/hooks';

interface DropdownRect {
  top: number;
  left: number;
  width: number;
  height: number;
  above: boolean;
}

interface UISelectInputProps
  extends Omit<HTMLAttributes<HTMLButtonElement>, 'onChange'> {
  value: SelectInputitem['id'] | SelectInputitem['label'] | null;
  label?: string;
  name?: string;
  placeholder?: string;
  options: SelectInputitem[];
  masterOptions?: SelectInputitem[];
  onChange?: (item: SelectInputitem) => void;
  required?: boolean;
  error?: string;
  touched?: boolean;
  className?: string;
  dropDownClassname?: string;
  /** 'below' = dropdown opens under trigger (default). 'above' = dropdown opens above trigger. */
  dropdownPosition?: 'below' | 'above';
  /** Render dropdown in a portal (document.body). Use when dropdown is clipped (e.g. tables). Default false. */
  usePortal?: boolean;
  wrapperClassName?: string;
  itemClassName?: string;
  itemTextClassName?: string;
  disabled?: boolean;
  leftIcon?: IconType;
  iconClassName?: string;
  labelClassName?: string;
  textClassName?: string;
  showSearch?: boolean;
  onDropdownInteract?: (state: boolean) => void;
}

export const SelectInput: React.FC<UISelectInputProps> = props => {
  const {
    value = null,
    className = '',
    dropDownClassname = '',
    dropdownPosition = 'below',
    usePortal = false,
    error = '',
    itemClassName = '',
    itemTextClassName = '',
    label = '',
    masterOptions = [],
    name = '',
    onBlur,
    onFocus,
    onChange,
    options = [],
    placeholder = '',
    required = false,
    touched = false,
    disabled = false,
    wrapperClassName,
    labelClassName = '',
    textClassName = '',
    leftIcon = '',
    iconClassName = '',
    showSearch = false,
    onDropdownInteract,
  } = props;

  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [inputState, setInputState] = useState<InputStateTypes>('default');
  const [dropdownRect, setDropdownRect] = useState<DropdownRect | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const inlineDropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownSearch, setDropdownSearch] = useState('');

  const closeDropdown = useCallback(() => {
    setDropdownOpen(false);
    setDropdownSearch('');
    onDropdownInteract?.(false);
    if (usePortal) setDropdownRect(null);
    updateInputState(error && touched ? 'error' : 'default');
  }, [error, touched, usePortal, onDropdownInteract]);

  const triggerRef = useClickOutside<HTMLButtonElement>(
    closeDropdown,
    usePortal ? dropdownRef : inlineDropdownRef,
  );

  const selectedItem = useMemo<SelectInputitem | null>(() => {
    const result = (
      options.find(option => option.id === value || option.label === value) ??
      null
    );
    if (!result && masterOptions.length > 0) {
      return masterOptions.find(option => option.id === value || option.label === value) ?? null;
    }
    return result;
  }, [value, options]);

  const textContent = useMemo(() => {
    return selectedItem ? (selectedItem.label ?? '') : '';
  }, [selectedItem]);

  const updateInputState = useCallback(
    (state: InputStateTypes) => {
      if (disabled) return;
      setInputState(state);
    },
    [disabled],
  );

  function onFocusHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState('focus');
    onFocus?.(e);
  }

  function onBlurHandler(e: FocusEvent<HTMLButtonElement>): void {
    updateInputState(error && touched ? 'error' : 'default');
    onBlur?.(e);
  }

  const handleSelect = useCallback(
    (option: SelectInputitem) => {
      onChange?.(option);
      closeDropdown();
      updateInputState(error && touched ? 'error' : 'default');
    },
    [onChange, closeDropdown, updateInputState, error, touched],
  );

  const measure = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const above = dropdownPosition === 'above';
    setDropdownRect({
      top: rect.top,
      left: rect.left,
      width: rect.width,
      height: rect.height,
      above,
    });
  }, [dropdownPosition]);

  const openDropdown = useCallback(() => {
    setDropdownOpen(true);
    onDropdownInteract?.(true);
  }, [onDropdownInteract]);

  const dropdownPositionClass =
    dropdownPosition === 'above' ? '!bottom-[104%] !top-auto' : 'top-[104%]';

  const filteredOptions = useMemo(
    () => options
      .filter(item =>
        item.label?.toLowerCase().includes(dropdownSearch.toLowerCase())
      )
      .sort((a, b) => (a.label || '').localeCompare(b.label || '')),
    [options, dropdownSearch],
  );

  const dropdownOptions = useMemo(
    () => (
      <>
        {showSearch && (
          <div className="sticky top-0 bg-white px-3 py-2 border-b border-ui_input_border z-10">
            <input
              type="text"
              value={dropdownSearch}
              onChange={e => setDropdownSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              onKeyDown={e => e.stopPropagation()}
              placeholder="Search..."
              className="w-full outline-none text-sm text-ui_blue font-FigtreeRegular placeholder:text-ui_input_border_dark"
              autoFocus
            />
          </div>
        )}
        {filteredOptions.length > 0 ? filteredOptions.map(item => (
        <button
          type="button"
          onClick={e => {
            e.stopPropagation();
            handleSelect(item);
          }}
          onMouseDown={e => e.preventDefault()}
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelect(item);
            }
          }}
          className={cn(
            'hover:bg-ui_sky_blue px-3 py-2 border-b border-ui_input_border last:border-b-0 cursor-pointer text-left w-full',
            selectedItem?.id === item.id && 'bg-ui_sky_blue',
            itemClassName,
          )}
          key={item.id}>
          <Text
            className={cn(
              'text-start font-FigtreeMeduim text-ui_blue text-base',
              selectedItem?.id === item.id && 'font-FigtreeBold',
              itemTextClassName,
            )}>
            {item.label}
          </Text>
        </button>
      )) : (
          <div className="px-3 py-4 text-center">
            <Text className="text-ui_input_border_dark text-sm font-FigtreeRegular">
              No results found
            </Text>
          </div>
        )}
      </>
    ),
    [filteredOptions, selectedItem, itemClassName, itemTextClassName, handleSelect, dropdownSearch],
);

  useLayoutEffect(() => {
    if (!usePortal || !dropdownOpen || !triggerRef.current) return;
    measure();
  }, [usePortal, dropdownOpen, measure]);

  useEffect(() => {
    if (!usePortal || !dropdownOpen) return;
    const onScrollOrResize = () => measure();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [usePortal, dropdownOpen, measure]);

  useEffect(() => {
    if (touched && error) updateInputState('error');
    else updateInputState('default');
  }, [error, touched]);

  const dropdownContent =
    usePortal &&
      dropdownOpen &&
      dropdownRect &&
      typeof document !== 'undefined' &&
      document.body
      ? createPortal(
        <div
          ref={dropdownRef}
          className={cn(
            'flex border max-h-[200px] overflow-y-auto overflow-x-hidden border-ui_input_border z-[9999] rounded-lg bg-white flex-col shadow-lg',
            dropDownClassname,
          )}
          style={{
            position: 'fixed',
            left: dropdownRect.left,
            width: dropdownRect.width,
            ...(dropdownRect.above
              ? {
                bottom: window.innerHeight - dropdownRect.top + 2,
                top: 'auto',
              }
              : { top: dropdownRect.top + dropdownRect.height + 2 }),
          }}>
          {dropdownOptions}
        </div>,
        document.body,
      )
      : null;

  const inlineDropdown =
    !usePortal &&
    dropdownOpen && (
      <div
        ref={inlineDropdownRef}
        onMouseDown={e => e.preventDefault()}
        className={cn(
          'flex border max-h-[200px] overflow-y-auto overflow-x-hidden w-full border-ui_input_border z-[999] rounded-lg bg-white flex-col absolute right-0 left-0',
          dropdownPositionClass,
          dropDownClassname,
        )}>
        {dropdownOptions}
      </div>
    );

  return (
    <div className={cn('flex flex-col gap-1 lg:gap-2 w-full relative', className)}>
      {label && (
        <Text
          variant="body"
          className={cn(
            '!font-FigtreeSemiBold inline-flex text-ui_blue gap-1',
            labelClassName,
          )}>
          {label}
          {required && <p className="text-rejection">*</p>}
        </Text>
      )}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => (dropdownOpen ? closeDropdown() : openDropdown())}
        disabled={disabled}
        onBlur={onBlurHandler}
        onFocus={onFocusHandler}
        className={cn(
          'border-2 flex px-3 py-2 lg:px-4 border-ui_input_border relative rounded-lg items-center disabled:cursor-not-allowed w-full',
          inputState === 'error' && 'border-rejection',
          inputState === 'focus' && 'border-black',
          disabled && '!border-ui_disabled',
          wrapperClassName,
        )}>
        {leftIcon && <Icon type={leftIcon} className={cn('size-4', iconClassName)} />}
        <Text
          className={cn(
            'grow text-start font-FigtreeRegular text-base max-w-full overflow-hidden text-ellipsis whitespace-nowrap',
            !textContent && 'text-ui_input_border_dark',
            textClassName,
            disabled && 'text-ui_disabled cursor-not-allowed',
          )}>
          {textContent || placeholder}
        </Text>
        {!disabled && <Icon type="cheveron_down" className="size-4" />}
      </button>
      {inlineDropdown}

      {dropdownContent}

      {touched && error && (
        <Text className="text-rejection text-sm">{error}</Text>
      )}
    </div>
  );
};
